import React from 'react';
import { LegalDoc, LegalBlock } from '../config/legalContent';

/**
 * Renders a legal document from config/legalContent.
 *
 * Content only — no page or modal chrome — so the Cosmos pages, the landing
 * pages and the landing modals can each keep their own wrapper while sharing
 * one copy of the text. Six inline copies is six chances for a legal document
 * to disagree with itself.
 */

const Body: React.FC<{ b: LegalBlock }> = ({ b }) => (
  <>
    {b.heading && (
      <h4 className="text-grayscale-1300 font-grotesk text-sm font-medium mt-3 mb-1.5">
        {b.heading}
      </h4>
    )}
    {(b.paras || []).map((p, i) => <p key={i}>{p}</p>)}
    {b.bullets && b.bullets.length > 0 && (
      <ul className="list-disc pl-6 space-y-2">
        {b.bullets.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    )}
    {b.table && (
      // Scrolls inside its own box: a two-column retention table is wider than
      // a phone, and letting it push the page sideways breaks every section.
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm border-collapse">
          <thead>
            <tr>
              {b.table.head.map(h => (
                <th key={h} className="border-b border-primary-200 py-2 pr-4 font-medium text-grayscale-1300">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.table.rows.map((r, i) => (
              <tr key={i}>
                {r.map((cell, j) => (
                  <td key={j} className="border-b border-grayscale-200 py-2 pr-4 align-top">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </>
);

export const LegalDocument: React.FC<{ doc: LegalDoc }> = ({ doc }) => (
  <div className="flex flex-col gap-4">
    <div className="bg-white rounded-xl p-4 border border-primary-200">
      <p className="text-sm leading-relaxed">
        <strong>Effective Date:</strong> {doc.effectiveDate}
      </p>
    </div>
    <div className="bg-white rounded-xl p-4 border border-primary-200">
      <p className="text-sm leading-relaxed"><strong>Operator:</strong> {doc.operator}</p>
    </div>
    <div className="bg-white rounded-xl p-4 border border-primary-200">
      <p className="text-sm leading-relaxed">
        <strong>Contact:</strong>{' '}
        <a href={`mailto:${doc.contact}`} className="text-primary-600 hover:text-primary-700">
          {doc.contact}
        </a>
      </p>
      <p className="text-sm leading-relaxed mt-1">
        <strong>Grievance Officer:</strong> {doc.grievanceOfficer}
      </p>
    </div>
    {doc.intro && doc.intro.length > 0 && (
      <div className="bg-white rounded-xl p-4 border border-primary-200">
        <div className="space-y-3 text-sm leading-relaxed">
          {doc.intro.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </div>
    )}
    {doc.sections.map(s => (
      <div key={s.heading} className="bg-white rounded-xl p-6 border border-primary-200">
        <h3 className="text-grayscale-1300 font-grotesk text-base font-medium mb-4">
          {s.heading}
        </h3>
        <div className="space-y-3 text-sm leading-relaxed">
          <Body b={{ paras: s.paras, bullets: s.bullets, table: s.table }} />
          {(s.blocks || []).map((b, i) => <Body key={i} b={b} />)}
        </div>
      </div>
    ))}
  </div>
);

export default LegalDocument;
